'use client';

import type {
  CommentWithRepliesFragment,
  ExportsXmpMetadata,
} from '@frameio/graphql-client/generated';
import { useClientNameContext } from '@frameio/shared/contexts/ClientNameContext';
import { useActiveViewableAssetId } from '@frameio/shared/hooks';
import { useGetActiveSequenceMetadataQuery } from '@frameio/shared/integrations/gql';
import { convertSecondsToMicroseconds } from '@frameio/utils/media/media';
import { useActiveDocumentId } from '../../../shared/contexts/ActiveHostEntitiesContext';
import { POINT_NOT_SET_VALUE } from '../../constants';

/** Timeline + sequence metadata used for Premiere marker-sync document alignment. */
type CommentMarkerSyncTimelineContext = {
  assetId: string | null | undefined;
  sequenceId: string | null | undefined;
  premiereSequenceMetadata: ReadonlyArray<unknown> | null | undefined;
};

function rowMatchesAsset(
  data: unknown,
  assetId: string | null | undefined
): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const row = data as { assetId?: string | null };
  return 'assetId' in row && row.assetId === assetId;
}

/** Premiere: active sequence export metadata row for `assetId`, if any. */
export function findExportMetadataForAsset({
  assetId,
  premiereSequenceMetadata,
}: {
  assetId: string;
  premiereSequenceMetadata: ReadonlyArray<unknown> | null | undefined;
}): ExportsXmpMetadata | null | undefined {
  const match = (premiereSequenceMetadata ?? []).find((data) =>
    rowMatchesAsset(data, assetId)
  );
  return match ? (match as ExportsXmpMetadata) : undefined;
}

/** Premiere: active sequence export metadata includes a row for this asset. */
function isPremiereActiveDocumentForCommentMarkerSync({
  assetId,
  premiereSequenceMetadata,
}: CommentMarkerSyncTimelineContext): boolean {
  return Boolean(
    premiereSequenceMetadata?.some((data) => rowMatchesAsset(data, assetId))
  );
}

export function shouldApplySequenceInOffset(
  meta: ExportsXmpMetadata | null | undefined
): meta is ExportsXmpMetadata & { sequenceIn: number } {
  return Boolean(
    meta &&
      'sequenceIn' in meta &&
      (meta as { sequenceIn?: number | null }).sequenceIn != null &&
      (meta as { sequenceIn: number }).sequenceIn !== POINT_NOT_SET_VALUE
  );
}

/** Premiere: comment time + sequence-in from the resolved export row. */
export function getPremiereCommentMarkerStartMicroseconds(
  comment: CommentWithRepliesFragment,
  exportMetadata: ExportsXmpMetadata | null | undefined
): number {
  const baseTimestamp = comment.timestampMicroseconds || 0;

  if (!exportMetadata || !shouldApplySequenceInOffset(exportMetadata)) {
    return baseTimestamp;
  }

  return (
    baseTimestamp +
    (convertSecondsToMicroseconds(exportMetadata.sequenceIn) || 0)
  );
}

type PremiereDataForCommentMarkerSync = {
  assetId: string | null | undefined;
  sequenceId: string | null | undefined;
  /**
   * When true, the UXP metadata query does not run. Used when the host is not
   * Premiere, on web, or when no asset context is needed.
   */
  skip: boolean;
};

/**
 * Premiere Pro: runs `GetActiveSequenceMetadata` (UXP) and derives whether the
 * viewable asset is linked to the active sequence export metadata.
 *
 * Pass `skip: true` to skip the query (e.g. web client or no asset). Apollo dedupes
 * the same query across callers; `readQuery` alone would not populate the cache on a
 * cold load and is not a substitute for this query.
 */
function usePremiereDataForCommentMarkerSync({
  assetId,
  sequenceId,
  skip,
}: PremiereDataForCommentMarkerSync): {
  isActiveSequenceLinkedToAsset: boolean;
  premiereSequenceMetadata: CommentMarkerSyncTimelineContext['premiereSequenceMetadata'];
} {
  const { data } = useGetActiveSequenceMetadataQuery({
    context: { resolveWithUXP: true },
    skip,
  });

  const premiereSequenceMetadata =
    data?.activeProject?.activeSequence?.metadata;

  if (skip) {
    return {
      isActiveSequenceLinkedToAsset: false,
      premiereSequenceMetadata: undefined,
    };
  }

  const isActiveSequenceLinkedToAsset =
    isPremiereActiveDocumentForCommentMarkerSync({
      assetId,
      sequenceId,
      premiereSequenceMetadata,
    });

  return {
    isActiveSequenceLinkedToAsset,
    premiereSequenceMetadata,
  };
}

/**
 * Premiere Pro comment-marker-sync state: viewable asset / active sequence context and
 * {@link usePremiereDataForCommentMarkerSync} (GetActiveSequenceMetadata + alignment).
 * After Effects does not run this hook (alignment is `Boolean(assetId)` in AE hooks).
 */
export function usePremiereCommentMarkerSyncState({
  assetId: assetIdOverride,
}: { assetId?: string | null } = {}): {
  exportMetadata: ExportsXmpMetadata | null | undefined;
  isActiveDocument: boolean;
} {
  const clientName = useClientNameContext();
  const assetIdFromView = useActiveViewableAssetId();
  const assetId = assetIdOverride ?? assetIdFromView;
  const sequenceId = useActiveDocumentId();

  const skip = clientName !== 'premiere' || !assetId;

  const premiereDoc = usePremiereDataForCommentMarkerSync({
    assetId,
    sequenceId,
    skip,
  });

  const premiereSequenceMetadata = premiereDoc.premiereSequenceMetadata;
  const exportMetadata = findExportMetadataForAsset({
    assetId: assetId ?? '',
    premiereSequenceMetadata,
  });

  return {
    exportMetadata,
    isActiveDocument: Boolean(
      assetId && premiereDoc.isActiveSequenceLinkedToAsset
    ),
  };
}
